/**
 * Governance Suite - Root Application Component
 *
 * Wraps the router with ErrorBoundary from @hello-world-dao/ui
 * for consistent error handling across the application.
 *
 * @see AC-3 - Suite imports from @hello-world-dao packages
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { ErrorBoundary } from '@hello-world-dao/ui';
import { AuthProvider } from '@hello-world-dao/auth';
import { router } from './router';
import {
  runIdentityCodeBootstrap,
  resetIdentityCodeBootstrap,
  isIdentityBootstrapInFlight,
} from './services/identity-code-bootstrap';
import { getIdentityJwt } from '@/services/identity-jwt-store';

/** Centered spinner while the identity-service session settles. */
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500" />
    </div>
  );
}

// bl-977: shown when the pre-mount silent /token/refresh TRANSIENTLY failed
// (timeout / 5xx / exhausted connection-retries) rather than definitively 401'd.
// A member holding a valid refresh cookie must NOT be bounced to the sign-in shell
// on a transient blip, so we hold this neutral loader and offer a manual retry
// instead of mounting the (unauthenticated) auth tree.
//
// bl-977 regression #4: the hold is now BOUNDED — see TRANSIENT_ESCALATION_CAP_MS.
// After the wall-clock ceiling elapses still-transient, App escalates to a mounted
// (unauthenticated) tree so a mis-classified logged-out visitor reaches the login
// shell instead of an indefinite loader. The manual Retry remains live throughout.
//
// UX COPY + STYLING ARE PLACEHOLDER — the spinner-longer-vs-login-flash tradeoff is
// Menley's lane (bl-977 all-three review flag). Do not treat this copy as final.
function ReconnectingLoader({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="min-h-screen flex flex-col items-center justify-center gap-4 text-gray-600"
    >
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500" />
      <p data-testid="reconnecting-message">Reconnecting…</p>
      <button
        type="button"
        onClick={onRetry}
        data-testid="reconnecting-retry"
        className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
      >
        Retry
      </button>
    </div>
  );
}

type BootstrapUiState = 'resolving' | 'settled' | 'transient';

// bl-977 regression #4 (2026-07-04): a HARD wall-clock ceiling on the transient
// "Reconnecting…" hold, measured from App mount (page load).
//
// The original bl-977 fix parked EVERY `transient` verdict on an INDEFINITE hold
// with a manual-only Retry. Under concurrent load (the very login-burst condition
// bl-977 fixed), a genuinely-logged-out visitor's `/token/refresh` can classify
// `transient` (timeout / 5xx) instead of a clean 401 — and then the visitor is
// stuck on the loader forever, never reaching the login shell (regression #4:
// 8 deterministic unauth-gating failures, "reconnecting…retry" captured where the
// login/forgot-password shell belonged). Pre-bl-977 they got the login shell
// instantly.
//
// So after this ceiling elapses with the bootstrap still parked in `transient`
// (or an in-flight retry still unresolved), we escalate to a mounted tree:
// AuthProvider mounts unauthenticated → ProtectedRoute lands the visitor on the
// login shell. This is a UI-VERDICT DOWNGRADE ONLY — it NEVER issues another
// `/token/refresh` (bl-789 reuse-safety is untouched; no extra token rotation).
//
// TRADEOFF (deliberate): a member holding a VALID refresh cookie who hits a real
// upstream outage lasting past this cap now also gets bounced to the login shell.
// That is strictly BETTER than pre-bl-977 (which bounced them INSTANTLY on the
// first blip) and is honestly the only safe terminal state — an indefinite hold
// is worse. The member keeps the manual Retry throughout the hold, and a fresh
// login re-establishes the session.
//
// VALUE (12s) — CORRECTED derivation (code-review, 2026-07-04): an earlier draft
// of this comment claimed the cap "sits ABOVE the bootstrap's ~8.4s worst-case
// bounded budget," implying a legitimately in-flight refresh could never be
// pre-empted. That figure only summed ONE retry backoff plus one timeout
// (400ms + 8000ms) and is WRONG — boundedRefresh's own state machine
// (DEFAULT_TIMEOUT_MS=8000, DEFAULT_MAX_RETRIES=2, backoff 400ms→800ms) allows
// THREE attempts (0,1,2) with TWO backoff delays before a terminal outcome:
//   - conservative bound (both retries fail via an instant connection-throw, only
//     the final attempt actually times out): 400 + 800 + 8000 = 9200ms (~9.2s).
//   - full bound (any attempt's connection-throw can legitimately land just
//     before its own 8s abort fires, so all three attempts can each consume
//     close to their full window): 8000×3 + 400 + 800 = 25,200ms (~25.2s) — this
//     is the SAME figure PR #168's own code-review independently derived for
//     boundedRefresh's worst case (see that review's focus item 5(a)).
// So the 12s cap does NOT sit above the bootstrap's full worst-case budget — it
// sits BELOW it (12s < 25.2s, and even the conservative 9.2s bound leaves under
// 3s of margin). The cap is chosen because it is comfortably BELOW the tightest
// unauth-gating regression predicate (15s; membership-profile TC-PROF-007/
// TC-NOTIF-004) so the mounted ProtectedRoute fallback ("Checking
// authentication…") / login bounce reliably lands inside that assertion window —
// NOT because it is provably safe against every legitimately-still-retrying
// connection. (NOT the ~20-25s the full boundedRefresh bound would call for:
// that exceeds the 15s predicate and would leave the 8 regression TCs red — the
// whole point of the fix.) HONEST TRADEOFF (widened from the earlier draft): a
// member holding a valid refresh cookie whose reconnect is still legitimately
// retrying WITHIN boundedRefresh's own designed bound (roughly 9.2s-25.2s, not
// just a "real outage" beyond that bound) can also be pre-empted by this cap and
// bounced to the login shell. This widened tradeoff must be reflected in the
// AC8 all-three UX review, not just the narrower "genuine outage" framing.
const TRANSIENT_ESCALATION_CAP_MS = 12_000;

// bl-1247: the ceiling on the AUTH VERDICT (distinct from TRANSIENT_ESCALATION_CAP_MS
// above, which un-parks the UI). Before bl-1247 the suite never passed
// `isInitialSessionResolving` to AuthProvider, so the bl-977 hold collapsed to a
// single `setTimeout(…, 0)`: at the 12s cap the tree force-mounted on an empty JWT
// store and ProtectedRoute bounced a member whose slow-but-succeeding `/token/refresh`
// was still in flight. Wiring the predicate (below) SPLITS mounting from deciding —
// the tree still mounts at 12s, but the unauthenticated verdict is now deferred while
// the refresh is genuinely in flight. AuthContext's hold has no ceiling of its own
// (it re-arms a 50ms poll for as long as the predicate returns true), so the bound
// lives here.
//
// VALUE (14s) — ruled by Coby 2026-08-06 (bl-1247). It is the LARGEST value that keeps
// total time-to-bounce strictly under the 15s unauth-gating regression predicate
// (TC-PROF-007 / TC-NOTIF-004), so NO ~/git/testing spec has to move. It strictly
// dominates the pre-bl-1247 behaviour (a 12.5s refresh now wins; before it lost at
// 12.003s) and covers boundedRefresh's CONSERVATIVE 9.2s bound with margin. It does
// NOT cover boundedRefresh's FULL 25.2s designed worst case — that residual honest
// tradeoff MOVES from 12s to 14s (see the TRANSIENT_ESCALATION_CAP_MS note); a refresh
// that legitimately needs longer than 14s is still pre-empted and bounced.
const SESSION_RESOLVE_CEILING_MS = 14_000;

// bl-1247: the page-load origin the SESSION_RESOLVE_CEILING_MS is measured from —
// the SAME origin as TRANSIENT_ESCALATION_CAP_MS (both armed at App mount, below).
// Null until App mounts; a null origin means the session-resolve hold is not active.
let sessionResolveOriginMs: number | null = null;

// bl-1247: the predicate wired into AuthProvider (`isInitialSessionResolving`).
// Reports that the INITIAL SESSION REFRESH is still genuinely in flight so
// AuthContext holds its unauthenticated verdict instead of bouncing a member who
// holds a valid `hwdao_refresh` cookie whose refresh is merely slow.
//
// ⚠️ Source discipline (do NOT "simplify" this):
//   - It reads the bootstrap module's OWN in-flight flag (isIdentityBootstrapInFlight),
//     NOT `bootstrapState` / `bootstrapStateRef` / any render value. The 12s
//     escalation FORCE-SETS bootstrapState to 'settled' (that is the only thing that
//     mounts AuthProvider with an unsettled refresh in the first place), so a
//     bootstrapState-derived predicate would return false at exactly the moment the
//     hold is needed — reproducing the bug through a different door with a fix that
//     looks correct. (bl-1247 Trap 1.)
//   - It is NOT `bootstrapPromise != null` (that never clears → parks forever). The
//     flag settles by either outcome. (bl-1247 Trap 2 / rule 1.)
//   - It is a STABLE module-scope function reference so it does not tear down and
//     restart AuthContext's poll on every AuthProvider re-render. (bl-1247 Trap 3.)
// The `&& elapsed < SESSION_RESOLVE_CEILING_MS` clause is the terminal bound: for
// every reachable input this returns false in bounded time, so the hold cannot become
// an indefinite loader (which would be bl-977 regression #4 through a new door).
function isInitialSessionResolving(): boolean {
  if (sessionResolveOriginMs === null) return false;
  return (
    isIdentityBootstrapInFlight() &&
    Date.now() - sessionResolveOriginMs < SESSION_RESOLVE_CEILING_MS
  );
}

function App() {
  // cross-suite-session-2026-06-18: mirror the dao-suite rehydrate so a member
  // who authenticated on another DAO suite (the shared httpOnly hwdao_refresh
  // cookie is Domain=.helloworlddao.com) is recognized here instead of being
  // bounced to /login. The identity-service JWT store is in-memory (empty on
  // every fresh cross-suite load) and AuthProvider resolves auth state ONCE at
  // mount, so the silent refresh must settle BEFORE AuthProvider reads the store.
  //
  // runIdentityCodeBootstrap handles both entry paths under one single-flight
  // promise: a `?code=` login-return (exchange) and a plain reload / cross-suite
  // first load (silent POST /token/refresh from the cookie).
  //
  // bl-977 / bl-985: the bootstrap now returns a CLASSIFIED verdict, not just
  // done/undone. While it resolves we hold PageLoader. On a definitive verdict
  // (`authed` or `unauthenticated`) we mount the auth tree (an unauthenticated
  // verdict lets ProtectedRoute redirect to the login shell — correct). On a
  // `transient` failure (timeout / 5xx / exhausted connection-retries) we hold a
  // neutral "Reconnecting…" loader with a manual retry instead of painting the
  // sign-in shell — a member with a valid refresh cookie is never bounced on a blip.
  const [bootstrapState, setBootstrapState] = useState<BootstrapUiState>('resolving');
  // Bumped by the manual retry to re-fire the bootstrap effect after the
  // single-flight is reset (StrictMode-safe: the initial mount does NOT reset, so
  // the double-invoked initial effect still shares the one singleton promise).
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);

  // E2E bypass (Playwright CI): ProtectedRoute already lets tests through without
  // a real session, and the local-preview env has no CORS-allowed identity-service
  // — the silent refresh throws, which would classify `transient` and hold the
  // Reconnecting loader forever. Under the bypass a transient verdict proceeds to
  // mount instead. Specs that DO need an authed session stub `/token/refresh` and
  // land `authed` before this mapping matters. Never true in a real build.
  const isE2EBypass = import.meta.env.VITE_E2E_AUTH_BYPASS === 'true';

  // bl-977 review hardening: a synchronous (not React-state) lock so a retry
  // cannot be re-entered while the current bootstrap cycle (initial or a
  // prior retry) is still unsettled — see handleReconnectRetry below for why
  // this must be a ref, not just the `bootstrapState === 'transient'` render
  // gate the Retry button's visibility already relies on.
  const retryLockRef = useRef(false);

  // bl-977 regression #4: latch + state-mirror for the wall-clock escalation.
  //   - escalatedRef: once the ceiling fires we've forced a mounted tree; any
  //     LATER bootstrap resolution (e.g. a retry's `/token/refresh` that lands
  //     after the ceiling) must NOT override it — otherwise a late `transient`
  //     would un-mount the tree back to the Reconnecting hold, past the cap.
  //   - bootstrapStateRef mirrors the latest render's state so the fire-once
  //     escalation timer (created in a mount-only effect, without `bootstrapState`
  //     in its deps) can read the current value without re-arming on every change.
  const escalatedRef = useRef(false);
  const bootstrapStateRef = useRef<BootstrapUiState>(bootstrapState);
  bootstrapStateRef.current = bootstrapState;

  useEffect(() => {
    let cancelled = false;
    retryLockRef.current = true;
    void runIdentityCodeBootstrap().then((status) => {
      // Once the wall-clock ceiling has escalated, ignore a late resolution: the
      // tree is already (deliberately) mounted unauthenticated — a trailing
      // `transient` must not bounce it back to the hold.
      if (cancelled || escalatedRef.current) return;
      retryLockRef.current = false;
      setBootstrapState(status === 'transient' && !isE2EBypass ? 'transient' : 'settled');
    });
    return () => {
      cancelled = true;
    };
  }, [bootstrapAttempt, isE2EBypass]);

  // bl-977 regression #4: the HARD wall-clock ceiling (see
  // TRANSIENT_ESCALATION_CAP_MS above). Mount-only, single-flight: one timer per
  // page load. It fires exactly once and force-settles ONLY if the app is still
  // parked pre-mount (either holding `transient` or resolving an in-flight retry) —
  // if a definitive verdict already mounted the tree (`settled`), it is a no-op.
  // StrictMode's double-invoked effect is safe: the cleanup clears the first
  // timer, leaving a single live timer, and the escalation is idempotent
  // (escalatedRef latch + a same-value setState). It NEVER calls
  // resetIdentityCodeBootstrap / runIdentityCodeBootstrap — no token rotation
  // (bl-789 untouched); it is a pure UI-verdict downgrade.
  useEffect(() => {
    // bl-1247: arm the auth-verdict ceiling from the SAME page-load origin as the
    // escalation cap, so `isInitialSessionResolving` measures its bound from mount.
    sessionResolveOriginMs = Date.now();
    const timer = setTimeout(() => {
      if (escalatedRef.current || bootstrapStateRef.current === 'settled') return;
      escalatedRef.current = true;
      setBootstrapState('settled');
    }, TRANSIENT_ESCALATION_CAP_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleReconnectRetry = useCallback(() => {
    // bl-789 hardening (code-level, not just render-timing): refuse to start a
    // retry while the current bootstrap cycle is still unsettled. Two
    // concurrent `/token/refresh` calls would each try to rotate the SAME
    // httpOnly refresh cookie; the loser presents an already-consumed token →
    // `ReuseDetected` → the WHOLE token family is revoked (hard logout
    // everywhere) — strictly worse than the transient loader this is meant to
    // resolve. The Retry button is only rendered once the CURRENT cycle has
    // already settled to `transient`, so this is normally unreachable through
    // the UI — but that reachability argument rests on React's render timing,
    // not on code. `retryLockRef` is a synchronous flag (unlike `useState`, it
    // can't be "batched away"), so two invocations landing in the same tick —
    // e.g. a fast double-click, or a future second retry entry point — still
    // only ever start ONE cycle.
    if (retryLockRef.current) return;
    retryLockRef.current = true;
    // NB: this deliberately does NOT reset the wall-clock escalation ceiling
    // (TRANSIENT_ESCALATION_CAP_MS is absolute from page load). A member may
    // Retry within the window, but a retry that itself re-lands `transient` does
    // not push the deadline out — otherwise a stuck upstream could hold the loader
    // forever behind repeated clicks. The ceiling is the terminal guarantee.
    // Clear the settled `transient` single-flight so the next run re-attempts the
    // silent refresh, then re-fire the effect. (Reset is only ever called on an
    // explicit user retry — never on the initial mount — so the StrictMode
    // single-flight guarantee is preserved.)
    resetIdentityCodeBootstrap();
    setBootstrapState('resolving');
    setBootstrapAttempt((n) => n + 1);
  }, []);

  if (bootstrapState === 'resolving') {
    return <PageLoader />;
  }

  if (bootstrapState === 'transient') {
    return <ReconnectingLoader onRetry={handleReconnectRetry} />;
  }

  return (
    <ErrorBoundary>
      <AuthProvider
        config={{
          apiBaseUrl: import.meta.env.VITE_ORACLE_BRIDGE_URL,
          // dac-001-7: authMode 'identity-jwt' routes logout() to identity-service
          // /logout (ADR-024) instead of the legacy oracle-bridge /api/auth/logout,
          // and repoints the package II-login path at identity-service. identityServiceUrl
          // is REQUIRED in this mode; loginFrontDoorUrl set for completeness.
          authMode: 'identity-jwt',
          identityServiceUrl: import.meta.env.VITE_IDENTITY_SERVICE_URL,
          loginFrontDoorUrl: import.meta.env.VITE_LOGIN_FRONTDOOR_URL,
          // cross-suite-session-2026-06-18: activate the JWT seam. When the
          // pre-mount bootstrap stored a non-expired identity-service JWT,
          // AuthContext derives isAuthenticated + roles from its claims; when
          // null/expired the member is unauthenticated — auth@3.0.0
          // (authz-001-7b) retired the oracle-bridge cookie-session fallback,
          // the JWT is the sole gate. (Comment corrected in bl-985 review; the
          // pre-3.0.0 "falls back to the cookie session" wording was stale.)
          getIdentityJwt: () => getIdentityJwt(),
          // bl-1247: hold the unauthenticated verdict while the pre-mount silent
          // /token/refresh is still genuinely in flight (bounded by
          // SESSION_RESOLVE_CEILING_MS). Without this the bl-977 hold in
          // @hello-world-dao/auth collapses to a single setTimeout(…, 0) and a member
          // holding a valid hwdao_refresh cookie is bounced to the login shell the
          // macrotask after the 12s escalation force-mounts the tree. `isInitialSessionResolving`
          // is a STABLE module-scope reference sourced from the bootstrap's OWN in-flight
          // flag (NOT bootstrapState — see the Trap-1 note at the predicate definition).
          isInitialSessionResolving,
          e2eBypass: import.meta.env.VITE_E2E_AUTH_BYPASS === 'true',
          devBypass: import.meta.env.VITE_DEV_AUTH_BYPASS === 'true',
        }}
      >
        <RouterProvider router={router} />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
